import React from "react";
import AppLayout from "./actprep-ui/components/AppLayout";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import AuthenticatingPage from "./actprep-ui/pages/AuthenticatingPage";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { urls } from "./actprep-ui/utils/urls";
import ProgramManagementRouter from "./pages/ProgramManagement";
import CurriculumRouter from "./pages/Curriculum";
import Redirect from "./actprep-ui/components/Redirect/";
import { groups } from "./actprep-ui/constants/auth";
import ErrorPage from "./actprep-ui/pages/ErrorPage";
import useAccess from "./actprep-ui/hooks/auth/useAccess";
import { useProfile } from "./actprep-ui/context/ProfileContext";
import { CorporateFare, EmojiEvents, Schedule } from "@mui/icons-material";
import Dashboard from "./pages/Home/Dashboard/Dashboard";
import CoachingRouter from "./pages/Coaching";
import SkeletonLayout from "./actprep-ui/components/SkeletonLayout/SkeletonLayout";
import SchedulingRouter from "./pages/Scheduling";
import EmployeeManagementRouter from "./pages/EmployeeManagement";
import { lazy, Suspense } from "react";

const InstallPage = lazy(() => import("./pages/Install"));

function App() {
  const { isLoaded: isProfileLoaded } = useProfile();
  const access = useAccess({ requiredGroups: [groups.EMPLOYEES] });
  const isInstallRoute = window.location.pathname.startsWith("/install");
  if (
    !(
      isInstallRoute ||
      isProfileLoaded ||
      window.location.href.includes(urls.sso.redirect())
    )
  ) {
    return <SkeletonLayout />;
  }

  let navLinkSections = [
    {
      path: urls.admin.dashboard.base(),
      isDashboard: true,
    },
    {
      section: "Employee Management",
      icon: <CorporateFare />,
      path: urls.admin.employeeManagement.base(),
      links: [
        {
          label: "Company Tree",
          path: urls.admin.employeeManagement.companyTree(),
        },
      ],
    },
    {
      section: "Coaching",
      icon: <EmojiEvents />,
      path: urls.admin.coaching.base(),
      links: [
        {
          label: "Accountability",
          path: urls.admin.coaching.accountability(),
        },
        {
          label: "Master Sheet",
          path: urls.admin.coaching.master(),
        },
        {
          label: "Master Sheet V2",
          path: urls.admin.coaching.masterV2(),
        },
        {
          label: "Good News",
          path: urls.admin.coaching.goodNews("total-increase"),
        },
        {
          label: "Team Management",
          path: urls.admin.coaching.teamManagement(),
        },
      ],
    },
    {
      section: "Curriculum",
      icon: <SchoolOutlined />,
      path: urls.admin.curriculum.base(),
      links: [
        {
          label: "Curriculum Builder",
          path: urls.admin.curriculum.curriculumBuilder(),
        },
        { label: "Tests", path: urls.admin.curriculum.tests() },
        { label: "Challenges", path: urls.admin.curriculum.challenges() },
        { label: "Lessons", path: urls.admin.curriculum.lessons() },
        { label: "Categories", path: urls.admin.curriculum.categories() },
      ],
    },
    {
      section: "Student Management",
      icon: <GroupsOutlined />,
      path: urls.admin.programManagement.base(),
      links: [
        { label: "Orders", path: urls.admin.programManagement.orders() },
        {
          label: "Enrollment Processing",
          path: urls.admin.programManagement.enrollmentProcessing(),
        },
        {
          label: "Organizations",
          path: urls.admin.programManagement.organizations(),
        },
        { label: "Cohorts", path: urls.admin.programManagement.cohorts() },
        {
          label: "Automations",
          path: urls.admin.programManagement.automations(),
        },
      ],
    },
    {
      section: "Scheduling",
      icon: <Schedule />,
      path: urls.admin.scheduling.base(),
      links: [
        { label: "Mock ACT Events", path: urls.admin.scheduling.mockActs() },
        {
          label: "To-do Management",
          path: urls.admin.scheduling.toDoItems(),
        },
      ],
    },
  ];

  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}
    >
      <Routes>
        <Route path="/error/:errorCode/" element={<ErrorPage />} key={0} />
        <Route
          path="/oauth/callback/"
          element={<AuthenticatingPage authenticate={true} />}
        />
        <Route
          path="/install/*"
          element={
            <Suspense fallback={<SkeletonLayout />}>
              <InstallPage />
            </Suspense>
          }
        />
        <Route
          path="/*"
          element={
            <>
              {access.status == "accepted" && (
                <AppLayout navlinkSections={navLinkSections} appTitle="Admin">
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/coaching/*" element={<CoachingRouter />} />
                    <Route
                      path="/program/*"
                      element={<ProgramManagementRouter />}
                    />
                    <Route
                      path="/curriculum/*"
                      element={<CurriculumRouter />}
                    />
                    <Route
                      path="/scheduling/*"
                      element={<SchedulingRouter />}
                    />
                    <Route
                      path="/employees/*"
                      element={<EmployeeManagementRouter />}
                    />
                    <Route
                      path="*"
                      element={<Redirect to={urls.error(404)} />}
                    />
                  </Routes>
                </AppLayout>
              )}
              {access.status == "denied" && (
                <Navigate to="/error/403/" replace />
              )}
            </>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
